import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import { Inbox, Send, RefreshCw, Mail, Search, ChevronLeft, PenSquare, Reply, Forward, Paperclip, FileIcon } from "lucide-react";

export default function MailPage() {
  const { apiFetch, API } = useAuth();
  const [activeTab, setActiveTab] = useState("inbox");
  const [inboxEmails, setInboxEmails] = useState([]);
  const [sentEmails, setSentEmails] = useState([]);
  const [stats, setStats] = useState({ total_inbox: 0, total_sent: 0, unread: 0 });
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [composeOpen, setComposeOpen] = useState(false);
  const [replyOpen, setReplyOpen] = useState(false);
  const [composeTo, setComposeTo] = useState("");
  const [composeSubject, setComposeSubject] = useState("");
  const [composeBody, setComposeBody] = useState("");
  const [sending, setSending] = useState(false);
  const [dateFilter, setDateFilter] = useState(new Date().toISOString().slice(0, 10));

  const fetchEmails = useCallback(async () => {
    try {
      const dateParam = dateFilter ? `&date_filter=${dateFilter}` : "";
      const [inboxRes, sentRes, statsRes] = await Promise.all([
        apiFetch(`${API}/mail/inbox?limit=100${dateParam}`),
        apiFetch(`${API}/mail/sent?limit=100${dateParam}`),
        apiFetch(`${API}/mail/stats/summary`)
      ]);
      if (inboxRes.ok) { const d = await inboxRes.json(); setInboxEmails(d.emails); }
      if (sentRes.ok) { const d = await sentRes.json(); setSentEmails(d.emails); }
      if (statsRes.ok) setStats(await statsRes.json());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [apiFetch, API, dateFilter]);

  useEffect(() => { fetchEmails(); }, [fetchEmails]);

  // Auto-sync IMAP every 5 minutes
  useEffect(() => {
    const doAutoSync = async () => {
      try {
        await apiFetch(`${API}/mail/sync`, { method: "POST" });
        fetchEmails();
      } catch (e) { /* silent */ }
    };
    const interval = setInterval(doAutoSync, 300000); // 5 min
    return () => clearInterval(interval);
  }, [apiFetch, API, fetchEmails]);

  const syncMail = async () => {
    setSyncing(true);
    try {
      const res = await apiFetch(`${API}/mail/sync`, { method: "POST" });
      if (res.ok) {
        const d = await res.json();
        toast.success(`Synchronisation terminée: ${d.synced} nouveaux emails`);
        fetchEmails();
      } else {
        const err = await res.json();
        toast.error(err.detail || "Erreur de synchronisation");
      }
    } catch (e) { toast.error("Erreur de synchronisation"); }
    finally { setSyncing(false); }
  };

  const handleCompose = async () => {
    if (!composeTo || !composeSubject) { toast.error("Destinataire et objet requis"); return; }
    setSending(true);
    try {
      const htmlBody = composeBody.replace(/\n/g, "<br>");
      const res = await apiFetch(`${API}/mail/compose`, {
        method: "POST",
        body: JSON.stringify({ to: composeTo, subject: composeSubject, body: htmlBody })
      });
      if (res.ok) {
        toast.success("Email envoyé");
        setComposeOpen(false);
        setComposeTo(""); setComposeSubject(""); setComposeBody("");
        fetchEmails();
      } else {
        const err = await res.json();
        toast.error(err.detail || "Erreur d'envoi");
      }
    } catch (e) { toast.error("Erreur d'envoi"); }
    finally { setSending(false); }
  };

  const handleReply = async () => {
    if (!composeBody) { toast.error("Écrivez votre réponse"); return; }
    setSending(true);
    try {
      const htmlBody = composeBody.replace(/\n/g, "<br>");
      const res = await apiFetch(`${API}/mail/${selectedEmail.email_id}/reply`, {
        method: "POST",
        body: JSON.stringify({ body: htmlBody })
      });
      if (res.ok) {
        toast.success("Réponse envoyée");
        setReplyOpen(false);
        setComposeBody("");
        fetchEmails();
      } else {
        const err = await res.json();
        toast.error(err.detail || "Erreur d'envoi");
      }
    } catch (e) { toast.error("Erreur d'envoi"); }
    finally { setSending(false); }
  };

  const handleForwardGmail = async (emailId) => {
    try {
      const res = await apiFetch(`${API}/mail/${emailId}/forward-gmail`, { method: "POST" });
      if (res.ok) {
        toast.success("Email transféré vers Contact.jvc973@gmail.com");
        fetchEmails();
      } else {
        const err = await res.json();
        toast.error(err.detail || "Erreur");
      }
    } catch (e) { toast.error("Erreur de transfert"); }
  };

  const openEmail = async (em) => {
    if (!em.read) {
      const res = await apiFetch(`${API}/mail/${em.email_id}`);
      if (res.ok) {
        const updated = await res.json();
        setSelectedEmail(updated);
        // Update local state
        setInboxEmails(prev => prev.map(e => e.email_id === em.email_id ? { ...e, read: true } : e));
        setStats(prev => ({ ...prev, unread: Math.max(0, prev.unread - 1) }));
        return;
      }
    }
    setSelectedEmail(em);
  };

  const formatDate = (d) => {
    if (!d) return "-";
    const dt = new Date(d);
    const now = new Date();
    const isToday = dt.toDateString() === now.toDateString();
    if (isToday) return dt.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
    return dt.toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" });
  };

  const filterEmails = (emails) => {
    if (!searchTerm) return emails;
    const q = searchTerm.toLowerCase();
    return emails.filter(e =>
      e.subject?.toLowerCase().includes(q) ||
      e.from_addr?.toLowerCase().includes(q) ||
      e.from_name?.toLowerCase().includes(q) ||
      e.to_addr?.toLowerCase().includes(q)
    );
  };

  if (loading) {
    return (
      <div className="p-4 sm:p-8">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-slate-200 rounded w-48" />
          <div className="h-96 bg-slate-200 rounded-lg" />
        </div>
      </div>
    );
  }

  if (selectedEmail) {
    return (
      <div className="p-4 sm:p-8" data-testid="mail-detail-view">
        <Button variant="ghost" onClick={() => setSelectedEmail(null)} className="mb-4 text-slate-600" data-testid="mail-back-btn">
          <ChevronLeft className="w-4 h-4 mr-1" /> Retour
        </Button>
        <Card className="border-slate-200">
          <CardHeader className="pb-3">
            <div className="flex items-start justify-between">
              <div>
                <CardTitle className="text-lg font-semibold text-slate-900">{selectedEmail.subject}</CardTitle>
                <div className="flex gap-4 mt-2 text-sm text-slate-500">
                  <span><strong>De:</strong> {selectedEmail.from_name || selectedEmail.from_addr}</span>
                  <span><strong>A:</strong> {selectedEmail.to_name || selectedEmail.to_addr}</span>
                </div>
                <p className="text-xs text-slate-400 mt-1">{formatDate(selectedEmail.date)}</p>
              </div>
              <Badge className={selectedEmail.folder === "inbox" ? "bg-blue-50 text-blue-600 border-0" : "bg-emerald-50 text-emerald-600 border-0"}>
                {selectedEmail.folder === "inbox" ? "Reçu" : "Envoyé"}
              </Badge>
            </div>
            <div className="flex gap-2 mt-4">
              <Button variant="outline" size="sm" onClick={() => { setComposeBody(""); setReplyOpen(true); }} data-testid="reply-email-btn">
                <Reply className="w-4 h-4 mr-1" /> Répondre
              </Button>
              <Button variant="outline" size="sm" onClick={() => handleForwardGmail(selectedEmail.email_id)} className="text-orange-600 border-orange-200 hover:bg-orange-50" data-testid="forward-gmail-btn">
                <Forward className="w-4 h-4 mr-1" /> Transférer dans mon adresse Gmail
              </Button>
            </div>
          </CardHeader>
          <Separator />
          <CardContent className="pt-4">
            {/* Attachments */}
            {selectedEmail.attachments && selectedEmail.attachments.length > 0 && (
              <div className="mb-4 p-3 bg-slate-50 rounded-lg border border-slate-200" data-testid="email-attachments">
                <p className="text-xs font-semibold text-slate-600 mb-2 flex items-center gap-1.5">
                  <Paperclip className="w-3.5 h-3.5" />
                  {selectedEmail.attachments.length} pièce(s) jointe(s)
                </p>
                <div className="flex flex-wrap gap-2">
                  {selectedEmail.attachments.map((att, idx) => (
                    <div key={idx} className="flex items-center gap-1.5 bg-white px-3 py-1.5 rounded border border-slate-200 text-xs text-slate-700">
                      <FileIcon className="w-3.5 h-3.5 text-blue-500 flex-shrink-0" />
                      <span className="truncate max-w-[200px]">{att}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div
              className="prose prose-sm max-w-none email-content"
              dangerouslySetInnerHTML={{ __html: selectedEmail.body_html || "<p>Aucun contenu</p>" }}
            />
          </CardContent>
        </Card>

        {/* Reply Dialog */}
        <Dialog open={replyOpen} onOpenChange={setReplyOpen}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>Répondre à {selectedEmail.from_name || selectedEmail.from_addr}</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <div className="bg-slate-50 p-3 rounded text-xs text-slate-500">
                <strong>Objet:</strong> Re: {selectedEmail.subject}
              </div>
              <Textarea value={composeBody} onChange={e => setComposeBody(e.target.value)} placeholder="Écrivez votre réponse..." rows={6} data-testid="reply-body-input" />
              <Button onClick={handleReply} disabled={sending} className="w-full bg-slate-900 hover:bg-slate-800" data-testid="send-reply-btn">
                <Send className="w-4 h-4 mr-2" /> {sending ? "Envoi..." : "Envoyer la réponse"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-8" data-testid="mail-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-barlow text-2xl font-bold text-slate-900 tracking-tight uppercase">Messagerie</h1>
          <p className="text-sm text-slate-500 mt-0.5">contact@soudurejvc.com</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => { setComposeTo(""); setComposeSubject(""); setComposeBody(""); setComposeOpen(true); }} className="bg-orange-500 hover:bg-orange-600 text-white gap-2" data-testid="compose-mail-btn">
            <PenSquare className="w-4 h-4" /> Nouveau message
          </Button>
          <Button onClick={syncMail} disabled={syncing} variant="outline" className="gap-2" data-testid="sync-mail-btn">
            <RefreshCw className={`w-4 h-4 ${syncing ? "animate-spin" : ""}`} />
            {syncing ? "Sync..." : "Synchroniser"}
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <Card className="border-slate-200">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-9 h-9 bg-blue-50 rounded-md flex items-center justify-center">
              <Inbox className="w-4 h-4 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900 font-barlow">{stats.total_inbox}</p>
              <p className="text-xs text-slate-500">Reçus</p>
            </div>
            {stats.unread > 0 && (
              <Badge className="ml-auto bg-orange-500 text-white border-0 text-xs">{stats.unread} non lus</Badge>
            )}
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-9 h-9 bg-emerald-50 rounded-md flex items-center justify-center">
              <Send className="w-4 h-4 text-emerald-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900 font-barlow">{stats.total_sent}</p>
              <p className="text-xs text-slate-500">Envoyés</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-9 h-9 bg-slate-50 rounded-md flex items-center justify-center">
              <Mail className="w-4 h-4 text-slate-500" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900 font-barlow">{stats.total_inbox + stats.total_sent}</p>
              <p className="text-xs text-slate-500">Total</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search + Date filter */}
      <div className="flex gap-3 mb-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Rechercher par sujet, expéditeur..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className="pl-10"
            data-testid="mail-search-input"
          />
        </div>
        <div className="flex items-center gap-2">
          <Input
            type="date"
            value={dateFilter}
            onChange={e => setDateFilter(e.target.value)}
            className="w-44 h-10"
            data-testid="mail-date-filter"
          />
          <Button variant="outline" size="sm" onClick={() => setDateFilter("")} className="h-10 text-xs whitespace-nowrap" data-testid="mail-all-dates-btn">
            Tous
          </Button>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="mb-4">
          <TabsTrigger value="inbox" data-testid="tab-inbox">
            Boîte de réception ({inboxEmails.length})
          </TabsTrigger>
          <TabsTrigger value="sent" data-testid="tab-sent">
            Envoyés ({sentEmails.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="inbox">
          <Card className="border-slate-200">
            <CardContent className="p-0 divide-y divide-slate-100">
              {filterEmails(inboxEmails).length === 0 ? (
                <div className="p-12 text-center">
                  <Inbox className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-400">{searchTerm ? "Aucun résultat" : "Aucun email reçu"}</p>
                  <p className="text-xs text-slate-300 mt-1">Cliquez sur Synchroniser pour récupérer vos emails</p>
                </div>
              ) : (
                filterEmails(inboxEmails).map(em => (
                  <div
                    key={em.email_id}
                    className={`flex items-center gap-4 p-4 cursor-pointer transition-colors hover:bg-slate-50 ${!em.read ? "bg-blue-50/30" : ""}`}
                    onClick={() => openEmail(em)}
                    data-testid={`inbox-email-${em.email_id}`}
                  >
                    <div className={`w-2 h-2 rounded-full flex-shrink-0 ${!em.read ? "bg-blue-500" : "bg-transparent"}`} />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center justify-between">
                        <p className={`text-sm truncate ${!em.read ? "font-semibold text-slate-900" : "text-slate-700"}`}>
                          {em.from_name || em.from_addr}
                        </p>
                        <span className="text-xs text-slate-400 flex-shrink-0 ml-4">{formatDate(em.date)}</span>
                      </div>
                      <p className={`text-sm truncate mt-0.5 ${!em.read ? "font-medium text-slate-800" : "text-slate-500"}`}>
                        {em.subject}
                      </p>
                      {em.attachments && em.attachments.length > 0 && (
                        <span className="inline-flex items-center gap-0.5 text-[10px] text-slate-400 mt-0.5">
                          <Paperclip className="w-3 h-3" /> {em.attachments.length}
                        </span>
                      )}
                    </div>
                  </div>
                ))
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="sent">
          <Card className="border-slate-200">
            <CardContent className="p-0 divide-y divide-slate-100">
              {filterEmails(sentEmails).length === 0 ? (
                <div className="p-12 text-center">
                  <Send className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-400">{searchTerm ? "Aucun résultat" : "Aucun email envoyé"}</p>
                </div>
              ) : (
                filterEmails(sentEmails).map(em => (
                  <div
                    key={em.email_id}
                    className="flex items-center gap-4 p-4 cursor-pointer transition-colors hover:bg-slate-50"
                    onClick={() => openEmail(em)}
                    data-testid={`sent-email-${em.email_id}`}
                  >
                    <div className="w-2 h-2 rounded-full flex-shrink-0 bg-emerald-400" />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center justify-between">
                        <p className="text-sm text-slate-700 truncate">
                          <span className="text-slate-400">A:</span> {em.to_name || em.to_addr}
                        </p>
                        <span className="text-xs text-slate-400 flex-shrink-0 ml-4">{formatDate(em.date)}</span>
                      </div>
                      <p className="text-sm text-slate-500 truncate mt-0.5">{em.subject}</p>
                      {em.attachments && em.attachments.length > 0 && (
                        <span className="inline-flex items-center gap-0.5 text-[10px] text-slate-400 mt-0.5">
                          <Paperclip className="w-3 h-3" /> {em.attachments.length} pièce(s) jointe(s)
                        </span>
                      )}
                    </div>
                  </div>
                ))
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <p className="text-[10px] text-slate-300 text-center mt-6">
        Synchronisation automatique toutes les 5 minutes + transfert vers Gmail
      </p>

      {/* Compose Dialog */}
      <Dialog open={composeOpen} onOpenChange={setComposeOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Nouveau message</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label>Destinataire</Label>
              <Input value={composeTo} onChange={e => setComposeTo(e.target.value)} placeholder="email@exemple.com" type="email" data-testid="compose-to-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Objet</Label>
              <Input value={composeSubject} onChange={e => setComposeSubject(e.target.value)} placeholder="Objet du message" data-testid="compose-subject-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Message</Label>
              <Textarea value={composeBody} onChange={e => setComposeBody(e.target.value)} placeholder="Rédigez votre message..." rows={8} data-testid="compose-body-input" />
            </div>
            <Button onClick={handleCompose} disabled={sending} className="w-full bg-slate-900 hover:bg-slate-800" data-testid="send-compose-btn">
              <Send className="w-4 h-4 mr-2" /> {sending ? "Envoi..." : "Envoyer"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
